"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y } from "swiper/modules";
import { ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import "swiper/css";
import "swiper/css/a11y";
const guidePages = [{
  id: 1,
  slug: "1_couverture",
  caption: "Couverture",
  src: "https://unevieensuede.com/wp-content/uploads/2026/09/1_couverture-scaled.png",
  srcset: "https://unevieensuede.com/wp-content/uploads/2026/09/1_couverture-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/guide_page_couverture.webp-wvc-sizes",
  alt: "Première page de couverture du guide d'installation en Suède"
}, {
  id: 2,
  slug: "2_Pourquoi-ce-guide",
  caption: "Pourquoi ce guide",
  src: "https://unevieensuede.com/wp-content/uploads/2026/09/2_Pourquoi-ce-guide-scaled.png",
  srcset: "https://unevieensuede.com/wp-content/uploads/2026/09/2_Pourquoi-ce-guide-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/guide_page_pourquoi_ce_guide.webp-wvc-sizes",
  alt: "Page introductive expliquant la démarche et les conseils du guide"
}, {
  id: 3,
  slug: "3_Le-calendrier-de-votre-installation",
  caption: "Le calendrier de votre installation",
  src: "https://unevieensuede.com/wp-content/uploads/2026/09/3_Le-calendrier-de-votre-installation-scaled.png",
  srcset: "https://unevieensuede.com/wp-content/uploads/2026/09/3_Le-calendrier-de-votre-installation-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/guide_page_calendrier_installation.webp-wvc-sizes",
  alt: "Page calendrier détaillant les étapes clés mois par mois"
}, {
  id: 4,
  slug: "4_Sommaire",
  caption: "Sommaire",
  src: "https://unevieensuede.com/wp-content/uploads/2026/09/4_Sommaire-scaled.png",
  srcset: "https://unevieensuede.com/wp-content/uploads/2026/09/4_Sommaire-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1682238/img/guide_page_sommaire.png-wvc-sizes",
  alt: "Page sommaire avec la liste complète des chapitres et démarches"
}];
export default function Preview() {
  const [selectedPage, setSelectedPage] = useState<(typeof guidePages)[number] | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="guidepreview" data-nav="light" className="bg-muted py-16 sm:py-20 md:py-28">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-2xl text-center">
          <h2 className="font-serif text-3xl font-normal tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Un aperçu du guide
          </h2>
          <div className="mx-auto mt-4 h-0.5 w-16 bg-secondary/60" />
          <p className="mt-4 font-sans text-base text-foreground/80 md:text-lg">
            Quelques pages pour vous faire une idée de ce qu'il contient
          </p>
        </motion.div>

        {/* Desktop Grid Layout */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 hidden gap-6 md:grid md:grid-cols-4 lg:gap-8">
          {guidePages.map((page, i) => <motion.div key={page.id} variants={itemVariants} data-index={i} className="group flex flex-col items-center">
              <div className="w-full overflow-hidden rounded-xl bg-background shadow-md transition-shadow duration-300 group-hover:shadow-lg">
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-background">
                  <button type="button" onClick={() => setSelectedPage(page)} aria-label={`Agrandir : ${page.caption}`} className="group cursor-pointer w-full h-full relative">
                    <div className="relative overflow-hidden rounded-lg h-full group-hover:opacity-75 transition-opacity">
                      <img src={page.src} data-wvc-srcset={page.srcset} data-wvc-sizes={page.sizes} alt={page.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                        <ZoomIn className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </button>
                </div>
              </div>
              <p className="mt-3 text-center font-mono text-sm text-muted-foreground">
                {page.caption}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Mobile Swiper Carousel */}
        <div className="mt-10 md:hidden">
          <Swiper modules={[A11y]} slidesPerView={1.2} spaceBetween={16} centeredSlides={false} grabCursor a11y={{
          prevSlideMessage: "Page précédente",
          nextSlideMessage: "Page suivante"
        }} className="w-full pb-4">
            {guidePages.map((page, i) => <SwiperSlide key={page.id} data-index={i}>
                <div className="flex flex-col items-center">
                  <div className="w-full overflow-hidden rounded-xl bg-background shadow-md">
                    <div className="relative aspect-[3/4] w-full overflow-hidden bg-background">
                      <button type="button" onClick={() => setSelectedPage(page)} aria-label={`Agrandir : ${page.caption}`} className="group cursor-pointer w-full h-full relative">
                        <div className="relative overflow-hidden rounded-lg h-full group-hover:opacity-75 transition-opacity">
                          <img src={page.src} data-wvc-srcset={page.srcset} data-wvc-sizes={page.sizes} alt={page.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                            <ZoomIn className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-center font-mono text-sm text-muted-foreground">
                    {page.caption}
                  </p>
                </div>
              </SwiperSlide>)}
          </Swiper>
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedPage} onOpenChange={open => !open && setSelectedPage(null)}>
        <DialogContent className="max-w-3xl w-full p-2 sm:p-4 bg-background">
          <DialogTitle className="sr-only">{selectedPage?.caption ?? "Aperçu de la page"}</DialogTitle>
          {selectedPage && <div className="space-y-2">
              <img src={selectedPage.src} data-wvc-srcset={selectedPage.srcset} data-wvc-sizes={selectedPage.sizes} alt={selectedPage.alt} className="w-full h-auto max-h-[85vh] object-contain rounded-lg" />
              <p className="mt-2 text-center font-mono text-sm text-muted-foreground">
                {selectedPage.caption}
              </p>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}